import React, { FC, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "lib/i18n/use-translation";

import {
  CompletedOn,
  CompletionError,
  MechanicProps,
  useCompletion,
} from "./shared";
import styles from "../../december-doors.module.scss";

const MAX_OTHER_LENGTH = 200;
const OTHER = "other";

interface ChoiceMechanicProps extends MechanicProps {
  options: string[];
  allowOther: boolean;
  choice: string | null;
}

const ChoiceMechanic: FC<ChoiceMechanicProps> = ({
  door,
  options,
  allowOther,
  choice,
  onComplete,
}) => {
  const { t } = useTranslation(["decemberDoors"]);
  const { submit, isSaving, hasFailed } = useCompletion(onComplete);
  const savedIsOther = choice !== null && !options.includes(choice);
  const [selected, setSelected] = useState<string | null>(
    savedIsOther ? OTHER : choice
  );
  const [otherText, setOtherText] = useState(savedIsOther ? choice ?? "" : "");

  const picked = selected === OTHER ? otherText.trim() : selected;
  const canSubmit =
    picked !== null && picked !== "" && picked !== choice && !isSaving;
  const name = `door-${door.id}-choice`;

  const renderTile = (value: string, label: string) => (
    <label
      key={value}
      className={clsx(
        styles["choice-tile"],
        selected === value && styles["choice-tile-selected"]
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected === value}
        onChange={() => setSelected(value)}
        className={styles["choice-input"]}
      />
      {label}
    </label>
  );

  return (
    <form
      className={styles.mechanic}
      onSubmit={(event) => {
        event.preventDefault();
        if (picked) submit({ choice: picked });
      }}
    >
      <fieldset className={styles["choice-group"]}>
        <legend className={styles["mechanic-label"]}>
          {t("decemberDoors:mechanic.choice.prompt")}
        </legend>
        <div className={styles["choice-tiles"]}>
          {options.map((option) => renderTile(option, option))}
          {allowOther &&
            renderTile(OTHER, t("decemberDoors:mechanic.choice.other"))}
        </div>
      </fieldset>
      {selected === OTHER && (
        <input
          type="text"
          className={styles["mechanic-input"]}
          aria-label={t("decemberDoors:mechanic.choice.other")}
          value={otherText}
          maxLength={MAX_OTHER_LENGTH}
          onChange={(event) => setOtherText(event.target.value)}
          // The field only appears once the learner asks for it.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={!savedIsOther}
        />
      )}
      <button
        type="submit"
        className={styles["mechanic-primary"]}
        disabled={!canSubmit}
      >
        {t("decemberDoors:mechanic.done")}
      </button>
      <CompletionError show={hasFailed} />
      {door.isCompleted && (
        <p className={styles["mechanic-confirm"]}>
          {t("decemberDoors:mechanic.doneConfirm")}
        </p>
      )}
      <CompletedOn door={door} />
    </form>
  );
};

export default ChoiceMechanic;
